import {createStore} from 'vuex'
import appService from '../js/appservice/index'
import userGroupsModule from './userGroups'
import testMethodsModule from './testmethods'
import scalesModule from './scales'
import instructionsModule from './instructions'
import testSuitesModule from './testsuite'
import imageBankModule from './imagebank'
import normGroupsModule from './normgroups'
import expertReportsModule from './expertreports'
import testDataModule from './testdata'
import { getErrorMessage } from '../js/helpers/storehelper'
import '@babel/polyfill'

const state = {
  isAuthenticated: false,
  permissionLevel: null,
  languages: [],
  defaultCulture: {},
  selectedCulture: {},
  userId: '',
  tokenRefreshTime: 0,
  siteCompanyLogo: '',
  siteCompanyName: '',
  appMessage: '',
  appMessageVariant: 'info',
  appMessageCountDown: 0,
  settings: {}
}

function saveSessionData(data) {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('token', data.token)
    window.sessionStorage.setItem('tokenExpiration', data.expiration)
    window.sessionStorage.setItem('userId', data.id)
    window.sessionStorage.setItem('permissionLevel', data.permissionLevel)
  }
}

const store = createStore({
  modules: {
    userGroupsModule,
    testMethodsModule,
    scalesModule,
    imageBankModule,
    instructionsModule,
    testSuitesModule,
    normGroupsModule,
    expertReportsModule,
    testDataModule
  },
  state,
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    permissionLevel: state => state.permissionLevel,
    languages: state => state.languages,
    selectedCulture: state => state.selectedCulture,
    defaultCulture: state => state.defaultCulture,
    userId: state => state.userId,
    hasSupplierPermissions: state => {
      return state.permissionLevel === 1
    },
    hasResellerPermissions: state => {
      return state.permissionLevel <= 2
    },
    hasCustomerPermissions: state => {
      return state.permissionLevel <= 3
    },
    siteCompanyLogo: state => state.siteCompanyLogo,
    siteCompanyName: state => state.siteCompanyName,
    isLisbergSite: state => {
      return state.siteCompanyName === 'lisberg'
    },
    appMessage: state => state.appMessage,
    appMessageVariant: state => state.appMessageVariant,
    appMessageCountDown: state => state.appMessageCountDown,
    settings: state => state.settings
  },
  actions: {
    getAppSettings(context) {
      return new Promise((resolve, reject) => {
        appService
          .getAppSettings()
          .then(data => {
            resolve(data)
            context.commit('appSettings', data)
          })
          .catch(error => {
            reject(getErrorMessage(error))
          })
      })
    },
    setSelectedCulture(context, selectedCulture) {
      context.commit('setSelectedCulture', selectedCulture)
    },
    setDefaultCulture(context, defaultCulture) {
      context.commit('setDefaultCulture', defaultCulture)
    },
    getLanguages(context) {
      return new Promise((resolve, reject) => {
        appService
          .getLanguages()
          .then(data => {
            resolve(data)
          })
          .catch(error => {
            reject(getErrorMessage(error))
          })
      })
    },
    setAppLanguages(context, languages) {
      context.commit('setAppLanguages', languages)
    },
    getSiteCompanyLogo(context, urlTag) {
      return new Promise((resolve, reject) => {
        appService
          .getCompanyLogo(urlTag)
          .then(data => {
            resolve(data)
          })
          .catch(error => {
            reject(getErrorMessage(error))
          })
      })
    },
    setSiteCompanyLogo(context, logo) {
      context.commit('setSiteCompanyLogo', logo)
    },
    setSiteCompanyName(context, name) {
      context.commit('setSiteCompanyName', name)
    },
    login(context, credentials) {
      return new Promise(resolve => {
        appService
          .login(credentials)
          .then(data => {
            context.commit('login', data)
            context.commit('setTokenRefreshTime')
            resolve(data)
          })
          .catch(() => {
            if (typeof window !== 'undefined') {
              window.alert('Could not login')
            }
          })
      })
    },
    logout(context) {
      context.commit('logout')
    },
    refreshToken(context) {
      if (context.state.isAuthenticated) {
        return new Promise(resolve => {
          appService
            .refreshToken()
            .then(data => {
              context.commit('login', data)
              context.commit('setTokenRefreshTime')
              resolve()
            })
            .catch(() => {
              context.commit('setTokenRefreshTime')
            })
        })
      } else {
        context.commit('setTokenRefreshTime')
      }
    },
    showMessage(context, message) {
      context.commit('setMessage', {
        message: message,
        variant: 'info',
        countDown: 3
      })
    },
    showErrorMessage(context, message) {
      context.commit('setMessage', {
        message: message,
        variant: 'danger',
        countDown: 3
      })
    },
    showSuccessMessage(context, message) {
      context.commit('setMessage', {
        message: message,
        variant: 'success',
        countDown: 3
      })
    },
    dismissCountDown(context, data) {
      context.commit('dismissCountDown', data)
    }
  },
  mutations: {
    setDefaultCulture(state, defaultCulture) {
      state.defaultCulture = defaultCulture
    },
    setSelectedCulture(state, selectedCulture) {
      state.selectedCulture = selectedCulture
    },
    setAppLanguages(state, result) {
      state.languages = result
    },
    setSiteCompanyLogo(state, logo) {
      state.siteCompanyLogo = logo
    },
    setSiteCompanyName(state, name) {
      state.siteCompanyName = name
    },
    login(state, result) {
      saveSessionData(result)
      state.isAuthenticated = true
      state.permissionLevel = parseInt(result.permissionLevel)
      state.userId = result.id
    },
    logout(state) {
      if (typeof window !== 'undefined') {
        window.sessionStorage.clear()
      }

      state.isAuthenticated = false
      state.userId = null
    },
    setTokenRefreshTime(state) {
      state.tokenRefreshTime = new Date().getTime() / 1000
    },
    setMessage(state, data) {
      state.appMessage = data.message
      state.appMessageVariant = data.variant
      state.appMessageCountDown = data.countDown
    },
    dismissCountDown(state, data) {
      state.appMessageCountDown = data
    },
    appSettings(state, data) {
      state.settings = data

      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('settings', JSON.stringify(data))
      }
    }
  }
})

if (typeof window !== 'undefined') {
  document.addEventListener('DOMContentLoaded', function(event) {
    let expiration = window.sessionStorage.getItem('tokenExpiration')
    var unixTimestamp = new Date().getTime() / 1000
    if (expiration !== null && parseInt(expiration) - unixTimestamp > 0) {
      store.state.isAuthenticated = true
      store.state.permissionLevel = parseInt(
        window.sessionStorage.getItem('permissionLevel')
      )
      store.state.userId = window.sessionStorage.getItem('userId')
      store.state.settings = JSON.parse(
        window.sessionStorage.getItem('settings')
      )
    }
  })

  document.addEventListener('click', function(event) {
    if (state.isAuthenticated) {
      var unixTimestamp = new Date().getTime() / 1000
      if (unixTimestamp - state.tokenRefreshTime > 10) {
        store.dispatch('refreshToken')
      }
    }
  })
}

export default store
